import {
  getAttributeKeys,
  getMemberKey,
  getAttributeKeyLabel,
  getRelationkeyLabel,
  getUid,
  splitUid,
  getNamespaceLabel,
} from 'Services/WeaverUtils'
import { traverseRecords } from './entities'
import { getGroupForType } from './groups'

const loadGraphDataFromNodes = (result) => {
  try {
    const res = traverseRecords(result, { showAttributes: false, showAllMetaNodes: false, loadedNodesMap: false }, [])

    return { abstract: false, ...res, groups: {} }
  } catch(e) {
    console.error(e)
  }
}

const loadGraphData = async (query) => {
  let results = []
  if (query instanceof Weaver.Sparql) {
    results = await query.findNodes()
  } else {
    results = await query
      .withAttributes()
      .withRelations()
      .alwaysLoadAttributes(getAttributeKeys())
      .setMetaNodesQuery(new Weaver.Query().selectIn('*').selectOut('*'))
      .find()
  }
  return results
}

const fetchNodeResult = async (id, memberKey) => {
  if (id) {
    const [idPart] = splitUid(id)
    return Weaver.MetaQueries.typesAndCountForId(idPart, getAttributeKeys(), memberKey, { limit: 99999 })
  }
  return Weaver.MetaQueries.typesAndCount(getAttributeKeys(), memberKey, { limit: 99999 })
}

const formatNodes = (nodeResult) => {
  return _.keyBy(nodeResult.map(({ uid, label, count, graph }) => {
    const formattedLabel = (label === 'null' || label === undefined) ? uid : label
    const id = getUid(uid, graph)
    return {
      wvrId: id,
      id,
      graph: splitUid(id)[1],
      label: `${getNamespaceLabel(formattedLabel)} (${count})`,
      group: uid,
      value: count,
      labels: []
    }
  }), 'id')
}

const calculateSelfReferenceSize = (edges, source, target) => {
  const selfReferenceCount = edges.filter(edge => edge.from === source && edge.to === target && target === source).length
  return (10 * selfReferenceCount) + 20
}

const formatEdges = (relationResult, nodes) => {
  return _.keyBy(relationResult.map(({ label, source, target, count, sourceGraph, targetGraph }) => {
    const sourceUid = getUid(source, sourceGraph)
    const targetUid = getUid(target, targetGraph)

    if (nodes[sourceUid] && nodes[targetUid]) {
      const id = `${sourceUid}->${label}->${targetUid}`
      return {
        id,
        from: sourceUid,
        to: targetUid,
        label: `${getRelationkeyLabel(label, id)} (${count})`,
        _label: `${getRelationkeyLabel(label, id)} (${count})`,
        key: getRelationkeyLabel(label, id),
        value: count,
        selfReference: {
          size: calculateSelfReferenceSize(relationResult, sourceUid, targetUid)
        },
        physics: sourceUid !== targetUid,
        scaling: {
          label: false,
          max: 6
        }
      }
    }
    return null
  }).filter(edge => edge !== null), 'id')
}

const loadAbstractGraphData = async (id, memberKey = getMemberKey()) => {
  const nodeResult = await fetchNodeResult(id, memberKey)
  const relationResult = await Weaver.MetaQueries.relationsAndCount(memberKey, { limit: 99999 })

  const nodes = formatNodes(nodeResult)
  const edges = formatEdges(relationResult, nodes)

  return { abstract: true, nodes, edges, groups: {} }
}

const loadAbstractAttributeData = async (key) => {
  const nodesResult = await new Weaver.Query()
    .exists(key)
    .unlimited()

  const sources = nodesResult.map((node) => getUid(node))
  const sourceCount = `${sources.length}`
  const sourceNode = {
    id: 'source',
    group: getGroupForType('undefined'),
    sources,
    label: `${getAttributeKeyLabel(key)} (${sourceCount})`,
    value: sourceCount
  }

  const nodes = _.keyBy([sourceNode], 'id')

  return { abstract: 'ATTRIBUTE_VIEW', nodes, edges: {}, relNodes: {}, groups: {} }
}

const loadAbstractRelationData = async (key) => {
  const nodesResult = await new Weaver.Query()
    .hasRelationOut(key)
    .unlimited()

  const sources = nodesResult.map((node) => getUid(node))
  const sourceCount = `${sources.length}`

  const targets = _.uniq(nodesResult.reduce((acc, node) => (
    [...acc, ...node.relation(key).all().map(getUid)]
  ), []))

  const targetCount = `${targets.length}`

  const sourceNode = {
    id: 'source',
    group: getGroupForType('undefined'),
    sources,
    label: sourceCount,
    value: sourceCount,
  }

  const targetNode = {
    id: 'target',
    group: getGroupForType('undefined'),
    targets,
    label: targetCount,
    value: targetCount,
  }

  const nodes = _.keyBy([sourceNode, targetNode], 'id')

  const edges = _.keyBy([{
    noAttributes: true,
    id: key,
    label: getRelationkeyLabel(key),
    from: 'source',
    to: 'target',
  }], 'id')

  return { abstract: 'RELATION_VIEW', nodes, edges, relNodes: {}, groups: {} }
}

export {
  loadGraphData,
  loadGraphDataFromNodes,
  loadAbstractGraphData,
  loadAbstractAttributeData,
  loadAbstractRelationData
}
